import { sbGetSetting, sbFetch } from "./supabaseClient";
import { renderLessonBodyHtml, parseMarkdownToBody } from "./lessonMarkdown";
import { parseMarkdown } from "./markdownParser";

export const exportAllContent = async () => {
  try {
    // Load all content
    const [lessons, prompts, resources, newsData] = await Promise.all([
      sbGetSetting("origamic-lessons").catch(() => []),
      sbGetSetting("origamic-prompts").catch(() => []),
      sbGetSetting("origamic-resources").catch(() => []),
      sbFetch("GET", "news?order=published_at.desc").catch(() => [])
    ]);

    const html = generateHTML({
      lessons: Array.isArray(lessons) ? lessons : [],
      prompts: Array.isArray(prompts) ? prompts : [],
      resources: Array.isArray(resources) ? resources : [],
      news: Array.isArray(newsData) ? newsData : []
    });

    downloadHTML(html, "content-export.html");
  } catch (err) {
    console.error("Error exporting content:", err);
    throw err;
  }
};

const generateHTML = ({ lessons, prompts, resources, news }) => {
  const timestamp = new Date().toLocaleString();

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Content Export</title>
  <style>
    * { box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      background: #050e1a;
      color: #e0e0e0;
      line-height: 1.6;
      margin: 0;
      padding: 20px;
    }
    .container { max-width: 900px; margin: 0 auto; background: #0f1829; padding: 40px; border-radius: 8px; border: 1px solid rgba(59, 130, 246, 0.1); box-shadow: 0 2px 8px rgba(0,0,0,0.3); }
    .header { border-bottom: 3px solid #5B9EF0; padding-bottom: 20px; margin-bottom: 40px; }
    .header h1 { margin: 0 0 10px 0; color: #5B9EF0; }
    .timestamp { color: #7A9CC4; font-size: 14px; }
    .section { margin-bottom: 60px; }
    .section-title {
      font-size: 28px;
      font-weight: 700;
      color: #5B9EF0;
      margin-bottom: 20px;
      padding-bottom: 10px;
      border-bottom: 2px solid rgba(59, 130, 246, 0.2);
    }
    .item { margin-bottom: 40px; padding-bottom: 30px; border-bottom: 1px solid rgba(59, 130, 246, 0.1); }
    .item:last-child { border-bottom: none; }
    .item-title {
      font-size: 20px;
      font-weight: 700;
      color: #e0e0e0;
      margin: 0 0 10px 0;
    }
    .item-meta {
      font-size: 13px;
      color: #7A9CC4;
      margin-bottom: 15px;
      display: flex;
      gap: 20px;
      flex-wrap: wrap;
    }
    .item-content {
      font-size: 15px;
      line-height: 1.8;
      color: rgba(255,255,255,0.85);
    }
    .item-content strong { color: #fff; font-weight: 700; }
    .item-content em { font-style: italic; }
    .item-content a { color: #5B9EF0; text-decoration: underline; }
    .item-content code {
      background: rgba(43,124,233,0.2);
      color: #5B9EF0;
      padding: 2px 6px;
      border-radius: 3px;
      font-family: monospace;
    }
    .item-content ul, .item-content ol { margin: 10px 0; padding-left: 30px; }
    .item-content li { margin: 5px 0; }
    .item-content input[type="checkbox"] {
      margin-right: 8px;
      cursor: default;
      pointer-events: none;
      accent-color: #22C55E;
    }
    .status-badge {
      display: inline-block;
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 600;
    }
    .status-draft { background: rgba(240,165,0,0.2); color: #F59E0B; }
    .status-published { background: rgba(34,197,94,0.2); color: #22C55E; }
    .status-scheduled { background: rgba(59,130,246,0.2); color: #5B9EF0; }
    h2, h3 { color: #5B9EF0; margin-top: 20px; margin-bottom: 10px; }
    .empty { color: #7A9CC4; font-style: italic; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>📚 Content Export</h1>
      <p class="timestamp">Exported on ${timestamp}</p>
    </div>

    ${lessons.length > 0 ? `
    <div class="section">
      <h2 class="section-title">📖 Lessons (${lessons.length})</h2>
      ${lessons.map(lesson => `
        <div class="item">
          <h3 class="item-title">${escapeHtml(lesson.title || "Untitled")}</h3>
          <div class="item-meta">
            <span class="status-badge status-${lesson.draft ? 'draft' : 'published'}">
              ${lesson.draft ? 'Draft' : 'Published'}
            </span>
            ${lesson.tag ? `<span>Tag: ${escapeHtml(lesson.tag)}</span>` : ''}
            ${lesson.publishedDate ? `<span>Published: ${new Date(lesson.publishedDate).toLocaleDateString()}</span>` : ''}
          </div>
          ${lesson.subtitle ? `<p style="color: #666; margin: 10px 0;">${escapeHtml(lesson.subtitle)}</p>` : ''}
          <div class="item-content">
            ${renderLessonContent(lesson)}
          </div>
        </div>
      `).join('')}
    </div>
    ` : ''}

    ${prompts.length > 0 ? `
    <div class="section">
      <h2 class="section-title">💭 Prompts (${prompts.length})</h2>
      ${prompts.map(prompt => `
        <div class="item">
          <h3 class="item-title">${escapeHtml(prompt.title || "Untitled")}</h3>
          <div class="item-meta">
            <span class="status-badge status-${prompt.draft ? 'draft' : 'published'}">
              ${prompt.draft ? 'Draft' : 'Published'}
            </span>
            ${prompt.publishedDate ? `<span>Published: ${new Date(prompt.publishedDate).toLocaleDateString()}</span>` : ''}
          </div>
          ${prompt.scenario ? `<div style="background: rgba(59, 130, 246, 0.1); border-left: 3px solid #5B9EF0; padding: 12px; margin: 15px 0; border-radius: 4px;"><strong style="color: #5B9EF0;">Scenario:</strong><div style="margin-top: 8px; color: rgba(255,255,255,0.85);">${parseMarkdown(prompt.scenario)}</div></div>` : ''}
          <div class="item-content">
            ${parseMarkdown(prompt.text || '')}
          </div>
        </div>
      `).join('')}
    </div>
    ` : ''}

    ${resources.length > 0 ? `
    <div class="section">
      <h2 class="section-title">🔗 Resources (${resources.length})</h2>
      ${resources.map(resource => `
        <div class="item">
          <h3 class="item-title">${escapeHtml(resource.title || "Untitled")}</h3>
          <div class="item-meta">
            <span class="status-badge status-${resource.draft ? 'draft' : 'published'}">
              ${resource.draft ? 'Draft' : 'Published'}
            </span>
            ${resource.publishedDate ? `<span>Published: ${new Date(resource.publishedDate).toLocaleDateString()}</span>` : ''}
          </div>
          ${resource.description ? `<p>${escapeHtml(resource.description)}</p>` : ''}
          <p><a href="${escapeHtml(resource.url)}" target="_blank">${escapeHtml(resource.url)}</a></p>
        </div>
      `).join('')}
    </div>
    ` : ''}

    ${news.length > 0 ? `
    <div class="section">
      <h2 class="section-title">📰 News (${news.length})</h2>
      ${news.map(item => `
        <div class="item">
          <h3 class="item-title">${escapeHtml(item.title || "Untitled")}</h3>
          <div class="item-meta">
            <span class="status-badge status-${item.archived ? 'draft' : 'published'}">
              ${item.archived ? 'Archived' : 'Published'}
            </span>
            ${item.published_at ? `<span>Published: ${new Date(item.published_at).toLocaleDateString()}</span>` : ''}
          </div>
          <div class="item-content">
            ${parseMarkdown(item.what_does_it_mean_to_me || '')}
          </div>
          ${item.url ? `<p><a href="${escapeHtml(item.url)}" target="_blank">${escapeHtml(item.url)}</a></p>` : ''}
        </div>
      `).join('')}
    </div>
    ` : ''}

    ${[lessons, prompts, resources, news].every(arr => arr.length === 0) ? '<p class="empty">No content found</p>' : ''}
  </div>
</body>
</html>`;
};

const renderLessonContent = (lesson) => {
  if (!lesson.body) return '<p class="empty">No content</p>';
  const bodyArray = typeof lesson.body === 'string' ? parseMarkdownToBody(lesson.body) : lesson.body;

  // Convert video blocks to clean links since iframes don't work in local HTML files
  let html = '';
  bodyArray.forEach(([type, content]) => {
    if (type === 'yt') {
      const match = content.match(/(?:v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]{11})/);
      const vid = match ? match[1] : content.trim().length === 11 ? content.trim() : null;
      if (vid) {
        html += `<p style="margin: 20px 0;"><a href="https://youtube.com/watch?v=${vid}" target="_blank" style="color: #5B9EF0; text-decoration: underline; font-size: 15px;">▶ Watch Video on YouTube</a></p>`;
      }
    } else {
      // Process other content types through renderLessonBodyHtml
      html += renderLessonBodyHtml([[type, content]]);
    }
  });

  return html || '<p class="empty">No content</p>';
};

const escapeHtml = (text) => {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

const downloadHTML = (html, filename) => {
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
